import { styled } from "@mui/material/styles";
import { useCookies } from "react-cookie";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import { SubmitButton } from "@/common/components/buttons/submit-button/SubmitButton";
import { FormShell } from "@/common/components/forms/FormShell";
import { PasswordField } from "@/common/components/forms/specific/password-field/PasswordField";
import { TextField } from "@/common/components/forms/text-field/TextField";
import { NO_VARIODOC_BANNER_COOKIE } from "@/core/precision/shared/constants";
import { EntryContainer } from "@/shared/containers/EntryContainer/EntryContainer";
import { useSnackbar } from "@/shared/containers/SnackbarProvider/SnackbarProvider";

import { getLoginErrorMessageId, useLoginMutation } from "./login.api";
import {
  LOGIN_INITIAL_VALUES,
  loginSchema,
  type LoginFormValues,
} from "./login.schema";
import { LoginLinks } from "./LoginLinks";

const Login = (): React.ReactElement => {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const [, , removeCookie] = useCookies([NO_VARIODOC_BANNER_COOKIE]);
  const loginMutation = useLoginMutation();

  const handleSubmit = (values: LoginFormValues) => {
    loginMutation.mutate(values, {
      onSuccess: () => {
        removeCookie(NO_VARIODOC_BANNER_COOKIE, { path: "/" });
        navigate("/farm");
      },
      onError: (error) => {
        showSnackbar({
          isError: true,
          message: <FormattedMessage id={getLoginErrorMessageId(error)} />,
        });
      },
    });
  };

  return (
    <EntryContainer>
      <FormWrapper>
        <FormShell<LoginFormValues>
          initialValues={LOGIN_INITIAL_VALUES}
          onSubmit={handleSubmit}
          validationSchema={loginSchema}
          footer={
            <FooterActions>
              <SubmitButton
                data-testid="login-submit"
                pending={loginMutation.isPending}
              >
                <FormattedMessage id="Login.log-in" />
              </SubmitButton>
              <LoginLinks />
            </FooterActions>
          }
        >
          <TextField
            autoComplete="username"
            autoFocus
            fullWidth
            inputMode="email"
            label={<FormattedMessage id="Login.username" />}
            name="username"
          />
          <PasswordField
            autoComplete="current-password"
            fullWidth
            label={<FormattedMessage id="Login.password" />}
            name="password"
          />
        </FormShell>
      </FormWrapper>
    </EntryContainer>
  );
};

const FormWrapper = styled("div")({
  margin: "16px 0 10px 0",
});

const FooterActions = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

export { Login };
